import { CommonModule } from '@angular/common';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { TranslateModule } from '@ngx-translate/core';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { HttpClient } from '@angular/common/http';
import { BsModalService, BsDropdownModule } from 'ngx-bootstrap';
import { Router, RouterModule } from '@angular/router';
import { __awaiter, __generator, __values, __extends } from 'tslib';
import { Injectable, NgModule, defineInjectable, inject, Directive, ElementRef, Input, Component, ViewChild } from '@angular/core';
import { UserExtensions, UserService } from '@s2a/ng-users';
import { AuthService } from '@s2a/ng-auth';
import { Logger, Environment, NgCommonModule, AbstractApiService, LanguageSelectionComponent, ThemeService, LunchlistService, BasePageComponent } from '@s2a/ng-common';

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
/** @type {?} */
var logger = Logger('s2a-app');
/**
 * Base class for platform applications
 *
 * - derives values for name, translationKey and route from app key
 * - provides link function for home page and platform header
 */
var  /**
 * Base class for platform applications
 *
 * - derives values for name, translationKey and route from app key
 * - provides link function for home page and platform header
 */
S2aApp = /** @class */ (function () {
    function S2aApp(key, enabledByRight, type, route, name, translationKey) {
        this.key = key;
        this.type = type;
        this.enabledByRight = enabledByRight;
        this.name = name ? name : key.split('-')
            .map(function (str) { return str.substr(0, 1).toUpperCase() + str.substr(1); })
            .join(' ');
        this.translationKey = translationKey ? translationKey : "apps." + key;
        this.route = route ? route : "/" + key;
    }
    /**
     * @param {?} user
     * @param {?} lunchList
     * @param {?} lunchlistForm
     * @return {?}
     */
    S2aApp.openLunchlist = /**
     * @param {?} user
     * @param {?} lunchList
     * @param {?} lunchlistForm
     * @return {?}
     */
    function (user, lunchList, lunchlistForm) {
        return __awaiter(this, void 0, void 0, function () {
            var llWindow, lunchlistSession, host, url;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        llWindow = window.open('about:blank', 'LunchlistWindow');
                        return [4 /*yield*/, lunchList.getSession()];
                    case 1:
                        lunchlistSession = _a.sent();
                        lunchlistForm.nativeElement.id.value = user.account;
                        host = window.location.host.replace('platform', 'lunchlist');
                        url = [window.location.protocol, host].join('//');
                        logger.info("lunchlist: " + url);
                        lunchlistForm.nativeElement.action = url;
                        lunchlistForm.nativeElement.token.value = lunchlistSession.lunchlistJWT;
                        lunchlistForm.nativeElement.submit();
                        llWindow.focus();
                        return [2 /*return*/];
                }
            });
        });
    };
    /**
     * @param {?} user
     * @return {?}
     */
    S2aApp.openClassic = /**
     * @param {?} user
     * @return {?}
     */
    function (user) {
        /** @type {?} */
        var host = window.location.host.replace('platform', user.account);
        /** @type {?} */
        var url = [window.location.protocol, host].join('//');
        logger.info("classic app: " + url);
        window.open(url, '_blank');
    };
    /**
     * @return {?}
     */
    S2aApp.prototype.getKey = /**
     * @return {?}
     */
    function () {
        return this.key;
    };
    /**
     * @param {?} currentApp
     * @param {?} user
     * @param {?} router
     * @param {?} lunchList
     * @param {?} lunchlistForm
     * @return {?}
     */
    S2aApp.prototype.open = /**
     * @param {?} currentApp
     * @param {?} user
     * @param {?} router
     * @param {?} lunchList
     * @param {?} lunchlistForm
     * @return {?}
     */
    function (currentApp, user, router, lunchList, lunchlistForm) {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                if (this.type === 'Local') {
                    this.openLocal(currentApp, router);
                }
                else if (this.type === 'External') {
                    this.openExternal();
                }
                else if (this.type === 'Classic') {
                    S2aApp.openClassic(user);
                }
                else if (this.type === 'Lunchlist') {
                    S2aApp.openLunchlist(user, lunchList, lunchlistForm);
                }
                else {
                    this.openDefault(currentApp, router);
                }
                return [2 /*return*/];
            });
        });
    };
    /**
     * Open a MicroApp:
     * - router navigation to root if the app opens itself (from home or app dropdown)
     * - change to MicroApp directory if you are outside the MicroApp
     * @param {?} currentApp
     * @param {?} router
     * @return {?}
     */
    S2aApp.prototype.openLocal = /**
     * Open a MicroApp:
     * - router navigation to root if the app opens itself (from home or app dropdown)
     * - change to MicroApp directory if you are outside the MicroApp
     * @param {?} currentApp
     * @param {?} router
     * @return {?}
     */
    function (currentApp, router) {
        if (currentApp && this.getKey() === currentApp.getKey()) {
            logger.info("local -> self: " + this.route);
            router.navigate(['/']);
        }
        else {
            logger.info("s2a -> local: " + this.route);
            window.location.href = this.route + "/";
        }
    };
    /**
     * @return {?}
     */
    S2aApp.prototype.openExternal = /**
     * @return {?}
     */
    function () {
        /** @type {?} */
        var url = this.route;
        logger.info("external route: " + url);
        window.open(url, '_blank');
    };
    /**
     * Open an internal app
     * - change to main directory if you currently are inside an MicroApp
     * - router navigation if your inside the main app
     * @param {?} currentApp
     * @param {?} router
     * @return {?}
     */
    S2aApp.prototype.openDefault = /**
     * Open an internal app
     * - change to main directory if you currently are inside an MicroApp
     * - router navigation if your inside the main app
     * @param {?} currentApp
     * @param {?} router
     * @return {?}
     */
    function (currentApp, router) {
        if (currentApp && currentApp.type === 'Local') {
            logger.info("local -> s2a: " + this.route);
            window.location.href = this.route + "/";
        }
        else {
            logger.info("s2a -> s2a: " + this.route);
            router.navigate([this.route]);
        }
    };
    return S2aApp;
}());

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
var AppRegistryService = /** @class */ (function () {
    function AppRegistryService(auth, environment) {
        var _this = this;
        this.auth = auth;
        this.environment = environment;
        this.apps = new BehaviorSubject([]);
        this.apps$ = this.apps.asObservable();
        this.appConfigurations = [];
        if (environment && environment.apps && environment.apps.length > 0) {
            this.initApps(environment);
        }
        this.auth.hasGlobalRights$.subscribe(function (_) {
            _this.apps.next(_this.filterApps());
        });
    }
    /**
     * Initialise apps from configuration and set current app
     *
     * @param {?} environment
     * @return {?}
     */
    AppRegistryService.prototype.initApps = /**
     * Initialise apps from configuration and set current app
     *
     * @param {?} environment
     * @return {?}
     */
    function (environment) {
        var e_1, _a;
        try {
            for (var _b = __values(environment.apps), _c = _b.next(); !_c.done; _c = _b.next()) {
                var app = _c.value;
                /** @type {?} */
                var s2aApp = new S2aApp(/** @type {?} */ (app.key), app.enabledByRight, /** @type {?} */ (app.type), app.route ? app.route : undefined, app.name ? app.name : undefined, app.translationKey ? app.translationKey : undefined);
                if (environment.appKey && app.key === environment.appKey) {
                    this.currentApp = s2aApp;
                }
                this.appConfigurations.push(s2aApp);
            }
        }
        catch (e_1_1) { e_1 = { error: e_1_1 }; }
        finally {
            try {
                if (_c && !_c.done && (_a = _b.return)) _a.call(_b);
            }
            finally { if (e_1) throw e_1.error; }
        }
    };
    /**
     * @return {?}
     */
    AppRegistryService.prototype.getApps = /**
     * @return {?}
     */
    function () {
        return this.apps$;
    };
    /**
     * @return {?}
     */
    AppRegistryService.prototype.getCurrentApp = /**
     * @return {?}
     */
    function () {
        return this.currentApp;
    };
    /**
     * @return {?}
     */
    AppRegistryService.prototype.filterApps = /**
     * @return {?}
     */
    function () {
        var _this = this;
        return this.appConfigurations.filter(function (app) { return _this.auth.hasGlobalRight(app.enabledByRight); });
    };
    AppRegistryService.decorators = [
        { type: Injectable, args: [{
                    providedIn: 'root'
                },] }
    ];
    /** @nocollapse */
    AppRegistryService.ctorParameters = function () { return [
        { type: AuthService },
        { type: Environment }
    ]; };
    /** @nocollapse */ AppRegistryService.ngInjectableDef = defineInjectable({ factory: function AppRegistryService_Factory() { return new AppRegistryService(inject(AuthService), inject(Environment)); }, token: AppRegistryService, providedIn: "root" });
    return AppRegistryService;
}());

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
var ConnectPlatformService = /** @class */ (function (_super) {
    __extends(ConnectPlatformService, _super);
    function ConnectPlatformService(http, env) {
        return _super.call(this, http, env, 'timelines/') || this;
    }
    /**
     * @param {?} equipmentIds
     * @return {?}
     */
    ConnectPlatformService.prototype.countProblems = /**
     * @param {?} equipmentIds
     * @return {?}
     */
    function (equipmentIds) {
        return __awaiter(this, void 0, void 0, function () {
            var err_1;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 2, , 3]);
                        return [4 /*yield*/, this.internalGet("problems/_count", { equipmentIds: equipmentIds.join(',') }).toPromise()];
                    case 1: return [2 /*return*/, (_a.sent()).data];
                    case 2:
                        err_1 = _a.sent();
                        console.error(err_1);
                        return [2 /*return*/, {}];
                    case 3: return [2 /*return*/];
                }
            });
        });
    };
    /**
     * @return {?}
     */
    ConnectPlatformService.prototype.countMyProblems = /**
     * @return {?}
     */
    function () {
        return __awaiter(this, void 0, void 0, function () {
            var err_2;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 2, , 3]);
                        return [4 /*yield*/, this.internalGet("problems/_count", { my: 1 }).toPromise()];
                    case 1: return [2 /*return*/, (_a.sent()).data["my"].problemCount];
                    case 2:
                        err_2 = _a.sent();
                        console.error(err_2);
                        return [2 /*return*/, 0];
                    case 3: return [2 /*return*/];
                }
            });
        });
    };
    ConnectPlatformService.decorators = [
        { type: Injectable, args: [{
                    providedIn: 'root'
                },] }
    ];
    /** @nocollapse */
    ConnectPlatformService.ctorParameters = function () { return [
        { type: HttpClient },
        { type: Environment }
    ]; };
    /** @nocollapse */ ConnectPlatformService.ngInjectableDef = defineInjectable({ factory: function ConnectPlatformService_Factory() { return new ConnectPlatformService(inject(HttpClient), inject(Environment)); }, token: ConnectPlatformService, providedIn: "root" });
    return ConnectPlatformService;
}(AbstractApiService));

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
var PlatformHeaderComponent = /** @class */ (function () {
    function PlatformHeaderComponent(auth, themeService, appRegistry, lunchList, modalService, router) {
        var _this = this;
        this.auth = auth;
        this.themeService = themeService;
        this.appRegistry = appRegistry;
        this.lunchList = lunchList;
        this.modalService = modalService;
        this.router = router;
        this.apps = [];
        this.getUserLetters = function () {
            return UserExtensions.getUserLetters(_this.auth.user);
        };
        this.getUserName = function () {
            return UserExtensions.getFullDisplayName(_this.auth.user);
        };
        this.getUserAccount = function () {
            return _this.auth.user.account;
        };
        this.logout = function () {
            _this.auth.logout();
        };
        this.toggleTheme = function () {
            _this.themeService.toggleTheme();
        };
        this.changeLanguage = function () {
            /** @type {?} */
            var config = {
                class: 'modal-sm',
            };
            _this.modalRef = _this.modalService.show(LanguageSelectionComponent, config);
        };
        this.openApp = function (app) { return __awaiter(_this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                app.open(this.appRegistry.getCurrentApp(), this.auth.user, this.router, this.lunchList, this.lunchlistForm);
                return [2 /*return*/];
            });
        }); };
    }
    /**
     * @return {?}
     */
    PlatformHeaderComponent.prototype.ngOnInit = /**
     * @return {?}
     */
    function () {
        var _this = this;
        this.auth.refreshUser();
        this.authSubscription = this.auth.verify();
        this.appSubscription = this.appRegistry.getApps().subscribe(function (apps) { return _this.apps = apps; });
    };
    /**
     * @return {?}
     */
    PlatformHeaderComponent.prototype.ngOnDestroy = /**
     * @return {?}
     */
    function () {
        this.authSubscription.unsubscribe();
        this.appSubscription.unsubscribe();
    };
    PlatformHeaderComponent.decorators = [
        { type: Component, args: [{
                    selector: 's2a-platform-header',
                    template: "<header class=\"platform-header\">\n  <div class=\"brand-section\" [routerLink]=\"['/home']\">\n    <svg-icon class=\"brand\" src=\"assets/img/brand.svg\"></svg-icon>\n    <svg-icon class=\"brand-mobile\" src=\"assets/img/brand-mobile.svg\"></svg-icon>\n  </div>\n\n  <!-- Nav left start -->\n  <nav class=\"header-nav-left\">\n    <div class=\"title-dropdown\" dropdown>\n      <div class=\"title-wrapper pl-2 pr-2 pr-sm-4 text-truncate\" dropdownToggle (click)=\"false\">\n        {{title}}\n        <svg-icon class=\"dropdown-icon d-none d-sm-block\" src=\"assets/icons/down-arrow.svg\"></svg-icon>\n      </div>\n      <ul class=\"header-service-grid dropdown-menu\" *dropdownMenu role=\"menu\">\n        <li class=\"grid-item active\" *ngFor=\"let app of apps\" (click)=\"openApp(app)\">\n          <img src=\"assets/apps/service-icon-{{app.key}}.svg\" alt=\"\">\n          <label translate>{{app.translationKey}}</label>\n          <form *ngIf=\"app.key === 'lunchlist'\" ngNoForm #lunchlistForm action=\"https://lunchlist.share2act-dev.io/\" method=\"POST\"\n            target=\"LunchlistWindow\">\n            <input type=\"hidden\" name=\"id\">\n            <input type=\"hidden\" name=\"token\">\n          </form>\n        </li>\n      </ul>\n    </div>\n  </nav>\n\n  <!-- Nav right start -->\n  <nav class=\"header-nav-right\">\n    <div class=\"user-dropdown\" dropdown>\n      <div class=\"toggle-wrapper\" dropdownToggle (click)=\"false\">\n        <!-- <div class=\"user-img\">\n          <img src=\"http://via.placeholder.com/50x50\" alt=\"\">\n        </div> -->\n        <div class=\"user-img\">\n          <span class=\"text-placeholder\">{{getUserLetters()}}</span>\n        </div>\n        <div class=\"user-info\">\n          <div class=\"user-name\">{{getUserName()}}</div>\n          <div class=\"user-account\">{{getUserAccount()}}</div>\n        </div>\n      </div>\n      <ul class=\"dropdown-menu dropdown-menu-right\" *dropdownMenu role=\"menu\">\n        <li class=\"dropdown-item\">\n          <a class=\"btn btn-link btn-block text-left\" routerLink=\"/user-management/user/me\" translate>components.platform_header.my_profile</a>\n        </li>\n        <li class=\"dropdown-divider\"></li>\n        <li class=\"dropdown-item\">\n          <a class=\"btn btn-link btn-block text-left\" (click)=\"toggleTheme()\" translate>components.platform_header.toggle_theme</a>\n        </li>\n        <li class=\"dropdown-divider\"></li>\n        <li class=\"dropdown-item\">\n          <a class=\"btn btn-link btn-block text-left\" (click)=\"changeLanguage()\" translate>components.platform_header.change_language</a>\n        </li>\n        <li class=\"dropdown-divider\"></li>\n        <li class=\"dropdown-item\">\n          <a class=\"btn btn-link btn-block text-left\" (click)=\"logout()\" translate>components.platform_header.logout</a>\n        </li>\n      </ul>\n    </div>\n\n    <!-- <div class=\"notification-toggle\">\n      <span class=\"notification-badge\">99+</span>\n      <svg-icon class=\"notification-icon\" src=\"assets/icons/notification.svg\"></svg-icon>\n    </div> -->\n  </nav>\n</header>\n",
                    styles: [":host .platform-header{position:fixed;top:0;left:0;width:100vw;height:67px;z-index:1000;box-shadow:0 2px 4px 0 rgba(0,0,0,.2)}.theme-light :host .platform-header{background-color:#fff;color:#525252}.theme-dark :host .platform-header{background-color:#142e4d;color:#fff}:host .platform-header .brand-section{vertical-align:top;display:inline-block;background-image:url(/assets/img/brand-bg.svg);background-repeat:no-repeat;background-size:cover;background-position:right bottom;height:67px;overflow:hidden;padding-right:25px;transition:width .3s linear;width:190px}:host .platform-header .brand-section:focus{box-shadow:none;outline:0}:host .platform-header .brand-section:hover{cursor:pointer}:host .platform-header .brand-section .brand{width:150px;margin-left:13px;margin-top:16px;transition:.3s}@media (max-width:767.98px){:host .platform-header .brand-section{width:84px}:host .platform-header .brand-section .brand{display:none}}:host .platform-header .brand-section .brand-mobile{width:150px;margin-left:15px;margin-top:16px;color:#fff}@media (min-width:768px){:host .platform-header .brand-section .brand-mobile{display:none}}:host .platform-header .header-nav-left{vertical-align:top;display:inline-block;position:relative}:host .platform-header .title-wrapper{padding-left:15px;padding-right:15px;font-size:18px;padding-top:20px;height:67px;-webkit-user-select:none;-moz-user-select:none;-ms-user-select:none;user-select:none;max-width:350px;position:relative;transition:.3s linear}:host .platform-header .title-wrapper:hover{cursor:pointer}.theme-light :host .platform-header .title-wrapper:hover{background-color:#f7f7f7}.theme-dark :host .platform-header .title-wrapper:hover{background-color:#193a61}:host .platform-header .title-wrapper .dropdown-icon{width:12px;position:absolute;top:20px;right:5px}@media (max-width:575.98px){:host .platform-header .title-wrapper{max-width:180px;font-size:14px;padding-top:23px}:host .platform-header .title-wrapper .dropdown-icon{top:25px}}:host .platform-header .header-service-grid{position:absolute;list-style:none;border-radius:0 15px;padding-top:30px;padding-left:30px;padding-bottom:15px;width:235px;max-height:80vh;overflow:auto;-webkit-overflow-scrolling:touch;box-shadow:0 2px 10px 0 rgba(0,0,0,.35)}.theme-light :host .platform-header .header-service-grid{background-color:#fff;color:#525252}.theme-dark :host .platform-header .header-service-grid{background-color:#142e4d;color:#fff}:host .platform-header .header-service-grid .grid-item{width:80px;display:inline-block;text-align:center;margin-right:15px;font-size:12px}:host .platform-header .header-service-grid .grid-item img{width:100%;transition:box-shadow .3s;border-radius:0 15px}.theme-light :host .platform-header .header-service-grid .grid-item img{background:linear-gradient(-135deg,#50bee4 0,#2874ba 100%)}.theme-dark :host .platform-header .header-service-grid .grid-item img{background:linear-gradient(-135deg,#50bee4 0,#2874ba 100%)}:host .platform-header .header-service-grid .grid-item label{margin-top:10px;margin-bottom:10px;width:100%;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}:host .platform-header .header-service-grid .grid-item.active label{font-weight:700}.theme-light :host .platform-header .header-service-grid .grid-item.active label{color:#525252}.theme-dark :host .platform-header .header-service-grid .grid-item.active label{color:#fff}@media print{.theme-dark :host .platform-header{background-color:#fff;color:#525252}.theme-dark :host .platform-header .title-wrapper:hover{background-color:#f7f7f7}.theme-dark :host .platform-header .header-service-grid{background-color:#fff;color:#525252}.theme-dark :host .platform-header .header-service-grid .grid-item img{background:linear-gradient(-135deg,#50bee4 0,#2874ba 100%)}.theme-dark :host .platform-header .header-service-grid .grid-item.active label{color:#525252}}:host .platform-header .header-service-grid .grid-item:hover{cursor:pointer}:host .platform-header .header-service-grid .grid-item:hover img{box-shadow:0 2px 10px 0 rgba(0,0,0,.35)}:host .header-nav-right{position:absolute;right:0;top:0}:host .user-dropdown{display:inline-block;height:67px;-webkit-user-select:none;-moz-user-select:none;-ms-user-select:none;user-select:none;transition:background-color .3s linear}:host .user-dropdown .btn.btn-link{text-decoration:none}.theme-light :host .user-dropdown:hover{background-color:#f7f7f7;cursor:pointer}.theme-dark :host .user-dropdown:hover{background-color:#193a61;cursor:pointer}:host .user-dropdown .user-img{margin-left:10px;display:inline-block;border-radius:0 15px;width:42px;height:42px;overflow:hidden;border:2px solid;margin-top:12px}.theme-light :host .user-dropdown .user-img{border-color:#d8d8d8}.theme-dark :host .user-dropdown .user-img{border-color:#fff}:host .user-dropdown .user-img .text-placeholder{font-weight:700;display:block;width:38px;height:38px;line-height:38px;text-align:center}.theme-light :host .user-dropdown .user-img .text-placeholder{background-color:#f7f7f7;color:#525252}.theme-dark :host .user-dropdown .user-img .text-placeholder{background-color:#193a61;color:#fff}:host .user-dropdown .user-img img{width:100%}:host .user-dropdown .user-info{display:inline-block;margin-left:10px;margin-right:10px;margin-top:14px;vertical-align:top}.theme-light :host .user-dropdown .user-info{color:#525252}.theme-dark :host .user-dropdown .user-info{color:#fff}@media print{.theme-dark :host .user-dropdown:hover{background-color:#f7f7f7;cursor:pointer}.theme-dark :host .user-dropdown .user-img{border-color:#d8d8d8}.theme-dark :host .user-dropdown .user-img .text-placeholder{background-color:#f7f7f7;color:#525252}.theme-dark :host .user-dropdown .user-info{color:#525252}}@media (min-width:768px){:host .user-dropdown .user-info{margin-right:25px}}:host .user-dropdown .user-info .user-name{font-size:14px;font-weight:700;max-width:200px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}:host .user-dropdown .user-info .user-account{font-size:14px;max-width:200px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}@media (max-width:767.98px){:host .user-dropdown .user-info .user-name{display:none}:host .user-dropdown .user-info .user-account{display:none}}:host .notification-toggle{display:inline-block;vertical-align:top;height:67px;padding-left:10px;padding-right:10px;width:60px;text-align:center;position:relative}.theme-light :host .notification-toggle:hover{background-color:#525252}.theme-dark :host .notification-toggle:hover{background-color:#fff}@media print{.theme-dark :host .notification-toggle:hover{background-color:#525252}}:host .notification-toggle:before{content:'';width:1px;height:60%;position:absolute;left:0;top:12px;opacity:.3}.theme-light :host .notification-toggle:before{background-color:#525252}.theme-dark :host .notification-toggle:before{background-color:#fff}:host .notification-toggle .notification-badge{color:#fff;min-width:18px;height:18px;font-size:12px;font-weight:700;display:inline-block;padding-left:5px;padding-right:5px;border-radius:20px;position:absolute;top:15px;right:12px;background-color:#b3282e}:host .notification-toggle .notification-icon{margin-top:20px;width:25px}.theme-light :host .notification-toggle .notification-icon{color:#525252}.theme-dark :host .notification-toggle .notification-icon{color:#fff}@media print{.theme-dark :host .notification-toggle:before{background-color:#525252}.theme-dark :host .notification-toggle .notification-icon{color:#525252}}"]
                }] }
    ];
    /** @nocollapse */
    PlatformHeaderComponent.ctorParameters = function () { return [
        { type: AuthService },
        { type: ThemeService },
        { type: AppRegistryService },
        { type: LunchlistService },
        { type: BsModalService },
        { type: Router }
    ]; };
    PlatformHeaderComponent.propDecorators = {
        title: [{ type: Input }],
        lunchlistForm: [{ type: ViewChild, args: ['lunchlistForm',] }]
    };
    return PlatformHeaderComponent;
}());

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
var HomeComponent = /** @class */ (function (_super) {
    __extends(HomeComponent, _super);
    function HomeComponent(appRegistry, auth, lunchList, timelineService, router) {
        var _this = _super.call(this) || this;
        _this.appRegistry = appRegistry;
        _this.auth = auth;
        _this.lunchList = lunchList;
        _this.timelineService = timelineService;
        _this.router = router;
        _this.apps = [];
        _this.timelineOpenProblemsCount = 0;
        _this.openApp = function (app) { return __awaiter(_this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                app.open(this.appRegistry.getCurrentApp(), this.auth.user, this.router, this.lunchList, this.lunchlistForm);
                return [2 /*return*/];
            });
        }); };
        return _this;
    }
    /**
     * @return {?}
     */
    HomeComponent.prototype.ngOnInit = /**
     * @return {?}
     */
    function () {
        return __awaiter(this, void 0, void 0, function () {
            var _a;
            var _this = this;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        this.appSubscription = this.appRegistry.getApps().subscribe(function (apps) { return _this.apps = apps; });
                        _a = this;
                        return [4 /*yield*/, this.timelineService.countMyProblems()];
                    case 1:
                        _a.timelineOpenProblemsCount = _b.sent();
                        return [2 /*return*/];
                }
            });
        });
    };
    /**
     * @return {?}
     */
    HomeComponent.prototype.ngOnDestroy = /**
     * @return {?}
     */
    function () {
        this.appSubscription.unsubscribe();
    };
    HomeComponent.decorators = [
        { type: Component, args: [{
                    selector: 's2a-dashboard',
                    template: "<s2a-platform-header title=\"{{ 'components.home.title' | translate }}\"></s2a-platform-header>\n<main>\n  <section class=\"content-main\">\n    <ul class=\"main-service-grid\">\n      <li class=\"grid-item\" *ngFor=\"let app of apps\" (click)=\"openApp(app)\">\n        <div class=\"img-container\">\n          <img src=\"assets/apps/service-icon-{{app.key}}.svg\" alt=\"\">\n          <span *ngIf=\"app.key === 'timeline' && timelineOpenProblemsCount\" class=\"problem-count\">\n            {{timelineOpenProblemsCount}}\n          </span>\n        </div>\n        <label translate>{{app.translationKey}}</label>\n        <form *ngIf=\"app.key === 'lunchlist'\" ngNoForm #lunchlistForm method=\"POST\" target=\"LunchlistWindow\">\n          <input type=\"hidden\" name=\"id\">\n          <input type=\"hidden\" name=\"token\">\n        </form>\n      </li>\n    </ul>\n  </section>\n  <!-- toggle class active -->\n  <!-- <aside class=\"sidebar\">\n    TODO: Notifications-List\n  </aside> -->\n</main>\n",
                    styles: [":host .content-main{display:flex;flex-direction:column;width:100%;height:100%}:host main{display:flex;flex-direction:row}:host .sidebar{flex-shrink:0;overflow-x:hidden;height:calc(100vh - 67px);width:0}.theme-light :host .sidebar{background-color:#fff}.theme-dark :host .sidebar{background-color:#142e4d}:host .sidebar.active{width:375px}@media (max-width:767.98px){:host .sidebar{position:fixed;display:block;top:67px;left:0;height:calc(100vh - 67px)}:host .sidebar.active{width:100vw}}:host .main-service-grid{list-style:none;padding:30px 0 30px 30px;-webkit-user-select:none;-moz-user-select:none;-ms-user-select:none;user-select:none}:host .main-service-grid .grid-item{width:150px;display:inline-block;text-align:center;margin-right:30px;font-size:12px;transition:.3s linear}@media (max-width:767.98px){:host .main-service-grid .grid-item{width:112px}}:host .main-service-grid .grid-item img{width:100%;transition:.3s;border-radius:0 15px}.theme-light :host .main-service-grid .grid-item img{background:linear-gradient(-135deg,#50bee4 0,#2874ba 100%)}.theme-dark :host .main-service-grid .grid-item img{background:linear-gradient(-135deg,#50bee4 0,#2874ba 100%)}:host .main-service-grid .grid-item label{margin-top:10px;margin-bottom:10px;width:100%;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}.theme-light :host .main-service-grid .grid-item label{color:#525252}.theme-dark :host .main-service-grid .grid-item label{color:#fff}@media print{.theme-dark :host .sidebar{background-color:#fff}.theme-dark :host .main-service-grid .grid-item img{background:linear-gradient(-135deg,#50bee4 0,#2874ba 100%)}.theme-dark :host .main-service-grid .grid-item label{color:#525252}}:host .main-service-grid .grid-item .img-container{position:relative;transition:.3s linear}:host .main-service-grid .grid-item .problem-count{position:absolute;top:-5px;right:-14px;background:#b3282e;border-radius:50%;width:25px;height:25px;display:flex;align-items:center;justify-content:center;color:#fff}:host .main-service-grid .grid-item:hover{cursor:pointer}:host .main-service-grid .grid-item:hover img{box-shadow:0 2px 10px 0 rgba(0,0,0,.35)}:host .main-service-grid .grid-item:hover .img-container{-webkit-transform:scale(1.02);transform:scale(1.02)}"]
                }] }
    ];
    /** @nocollapse */
    HomeComponent.ctorParameters = function () { return [
        { type: AppRegistryService },
        { type: AuthService },
        { type: LunchlistService },
        { type: ConnectPlatformService },
        { type: Router }
    ]; };
    HomeComponent.propDecorators = {
        lunchlistForm: [{ type: ViewChild, args: ['lunchlistForm',] }]
    };
    return HomeComponent;
}(BasePageComponent));

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
/** @type {?} */
var logger$1 = Logger('s2aHasRole');
var HasRoleDirective = /** @class */ (function () {
    function HasRoleDirective(element, userService, authService) {
        this.element = element;
        this.userService = userService;
        this.authService = authService;
    }
    Object.defineProperty(HasRoleDirective.prototype, "hash", {
        set: /**
         * @param {?} something
         * @return {?}
         */
        function (something) {
            this.checkVisibility();
        },
        enumerable: true,
        configurable: true
    });
    /**
     * @return {?}
     */
    HasRoleDirective.prototype.getCurrentUserData = /**
     * @return {?}
     */
    function () {
        return __awaiter(this, void 0, void 0, function () {
            var userData, user, ex_1, _a, _b;
            return __generator(this, function (_c) {
                switch (_c.label) {
                    case 0:
                        _c.trys.push([0, 1, , 4]);
                        userData = JSON.parse(localStorage.getItem('LOCALSTORAGE_USER_DATA'));
                        user = userData;
                        if (!user.lastname || !user.roles) {
                            throw new Error("Missing keys.");
                        }
                        // Add an userId as role to allow per-user rights
                        user.roles.global.push(user.userId);
                        return [2 /*return*/, user];
                    case 1:
                        ex_1 = _c.sent();
                        logger$1.error("Failed to read current user from localStorage:", ex_1);
                        _b = (_a = this.userService).getUser;
                        return [4 /*yield*/, this.authService.getUserId()];
                    case 2: return [4 /*yield*/, _b.apply(_a, [_c.sent()])];
                    case 3: return [2 /*return*/, _c.sent()];
                    case 4: return [2 /*return*/];
                }
            });
        });
    };
    /**
     * @return {?}
     */
    HasRoleDirective.prototype.ngOnInit = /**
     * @return {?}
     */
    function () {
        return __awaiter(this, void 0, void 0, function () {
            var _a;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        _a = this;
                        return [4 /*yield*/, this.getCurrentUserData()];
                    case 1:
                        _a.user = _b.sent();
                        this.checkVisibility();
                        return [2 /*return*/];
                }
            });
        });
    };
    /**
     * @return {?}
     */
    HasRoleDirective.prototype.checkVisibility = /**
     * @return {?}
     */
    function () {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                if (!this.user) {
                    return [2 /*return*/];
                }
                if (this.hasCommonElements(this.s2aHasRole, this.user.roles.global)) {
                    logger$1.info("hasCommonElements(...) = true");
                    this.element.nativeElement.style.display = '';
                }
                else {
                    logger$1.info("hasCommonElements(" + this.s2aHasRole + ", " + this.user.roles.global + ") = false");
                    this.element.nativeElement.style.display = 'none';
                }
                return [2 /*return*/];
            });
        });
    };
    /**
     * @param {?} arr1
     * @param {?} arr2
     * @return {?}
     */
    HasRoleDirective.prototype.hasCommonElements = /**
     * @param {?} arr1
     * @param {?} arr2
     * @return {?}
     */
    function (arr1, arr2) {
        if (!Array.isArray(arr1) || !Array.isArray(arr2)) {
            return false;
        }
        if (arr1.length > 0) {
            for (var i = 0; i < arr1.length; i++) {
                if (arr2.indexOf(arr1[i]) > -1) {
                    return true;
                }
            }
        }
        else {
            for (var i = 0; i < arr2.length; i++) {
                if (arr1.indexOf(arr2[i]) > -1) {
                    return true;
                }
            }
        }
        return false;
    };
    HasRoleDirective.decorators = [
        { type: Directive, args: [{
                    selector: '[s2aHasRole]'
                },] }
    ];
    /** @nocollapse */
    HasRoleDirective.ctorParameters = function () { return [
        { type: ElementRef },
        { type: UserService },
        { type: AuthService }
    ]; };
    HasRoleDirective.propDecorators = {
        s2aHasRole: [{ type: Input }],
        hash: [{ type: Input }]
    };
    return HasRoleDirective;
}());

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
var NgPlatformModule = /** @class */ (function () {
    function NgPlatformModule() {
    }
    /**
     * @return {?}
     */
    NgPlatformModule.forRoot = /**
     * @return {?}
     */
    function () {
        return {
            ngModule: NgPlatformModule,
            providers: [
                AppRegistryService,
                ConnectPlatformService
            ]
        };
    };
    NgPlatformModule.decorators = [
        { type: NgModule, args: [{
                    imports: [
                        CommonModule,
                        AngularSvgIconModule,
                        TranslateModule,
                        NgCommonModule,
                        RouterModule,
                        BsDropdownModule,
                    ],
                    declarations: [
                        PlatformHeaderComponent,
                        HomeComponent,
                        HasRoleDirective,
                    ],
                    exports: [
                        PlatformHeaderComponent,
                        HomeComponent,
                        HasRoleDirective,
                    ]
                },] }
    ];
    return NgPlatformModule;
}());

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */

export { NgPlatformModule, S2aApp, AppRegistryService, ConnectPlatformService, PlatformHeaderComponent, HomeComponent, HasRoleDirective };

//# sourceMappingURL=s2a-ng-platform.js.map